import React from 'react';

const LicenseAgreementPage: React.FC = () => {
  return (
    <div className="p-6 max-w-4xl mx-auto bg-surface-card shadow-md rounded-xl mt-6">
      <h1 className="font-display tracking-tight text-3xl font-bold mb-6 text-text-primary">Sync EHS Software as a Service (SaaS) License & Use Agreement</h1>
      <p className="text-sm text-text-muted mb-6">Last Updated: February 12, 2026</p>

      <div className="space-y-4 text-text-body">
        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">1. Scope of Agreement</h2>
          <p>
            This Software as a Service Agreement ("Agreement") is made between Sync EHS, LLC ("Sync EHS," "We," or "Us") and the user ("You," "Subscriber," or "Your"), including all authorized users under Your account, who accesses, subscribes to, or uses the Sync EHS platform (the "Platform"), which includes the web application, APIs, dashboards, reports, and related services provided by Sync EHS for safety management.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">2. License, Access & Intellectual Property</h2>
          <p>
            Sync EHS grants You a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for internal safety management purposes, subject to the terms of this Agreement.
          </p>
          <p className="mb-2">
            <strong>Intellectual Property Ownership.</strong> The Platform, including all source code, object code, algorithms, database designs, user interface designs, workflows, business logic, documentation, and all related intellectual property, is and shall remain the sole and exclusive property of Sync EHS, LLC. The license granted herein is limited to access and use of the Platform as a service and does not convey any ownership rights in the Platform or its underlying technology.
          </p>
          <p className="mb-2">
            <strong>Restrictions.</strong> You agree that You shall not, directly or indirectly, and shall not permit any third party to:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>(a) Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, algorithms, data structures, or underlying ideas of the Platform;</li>
            <li>(b) Copy, reproduce, or replicate the Platform or any of its features, functions, modules, workflows, user interface designs, or data structures, in whole or in part;</li>
            <li>(c) Create derivative works based on the Platform or any part thereof, including any software, application, or service that is substantially similar to, imitates, or replicates the functionality of the Platform;</li>
            <li>(d) Use the Platform to develop any competing product or service, or assist any third party in doing so;</li>
            <li>(e) Remove, alter, or obscure any copyright, trademark, or other proprietary notices contained in or on the Platform;</li>
            <li>(f) Sublicense, resell, share, transfer, or otherwise make available Your account credentials or access to the Platform to any person or entity not authorized by Sync EHS, LLC;</li>
            <li>(g) Use automated tools, including bots, scrapers, spiders, or crawlers, to access, index, or extract data or content from the Platform;</li>
            <li>(h) Use the Platform in a manner that exceeds reasonable usage limits (e.g., excessive API calls or data uploads that impact service performance).</li>
          </ul>
          <p>
            Sync EHS reserves the right to monitor usage and suspend access if usage violates this Agreement or disrupts service for other users.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">3. Feedback</h2>
          <p>
            Any suggestions, enhancement requests, recommendations, or other feedback provided by You regarding the Platform shall become the exclusive property of Sync EHS, LLC. Sync EHS, LLC shall be free to use, incorporate, and commercialize such feedback without restriction or compensation.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">4. Confidentiality</h2>
          <p>
            You acknowledge that the Platform's features, functionality, user interfaces, and technical architecture constitute confidential and proprietary information of Sync EHS, LLC. You shall not disclose such information to any third party for the purpose of enabling the development of a competing product or service.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">5. No Warranty or Safety Guarantee</h2>
          <p>
            The Platform is provided "as-is" and "as-available," without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            Sync EHS provides tools to track, record, and analyze safety-related data but does not assume any responsibility for Your organization's compliance, personnel safety, or regulatory standing.
          </p>
          <p>
            Sync EHS does not provide legal, safety, or compliance advice, and using the Platform does not guarantee OSHA compliance or prevent workplace injuries.
          </p>
          <p>
            You are solely responsible for interpreting the data, enforcing safety policies, training Your employees, and ensuring compliance with applicable laws and regulations.
          </p>
          <p>
            Sync EHS disclaims all liability for injuries, fatalities, or regulatory violations arising from the use (or non-use) of the Platform.
          </p>
          <p>
            Sync EHS does not guarantee uninterrupted or error-free access to the Platform and may perform maintenance or updates that temporarily affect availability.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">6. User Responsibilities</h2>
          <p>
            You must ensure that only authorized personnel access Your company's account and that all users comply with this Agreement.
          </p>
          <p>
            You are responsible for the accuracy, legality, and completeness of all data entered into the Platform, and You must comply with all applicable laws, including OSHA regulations and data protection laws (e.g., GDPR, CCPA).
          </p>
          <p>
            You agree not to upload malicious, unauthorized, or false information, or use the Platform for any illegal or harmful purpose.
          </p>
          <p>
            You must promptly notify Sync EHS at support@syncehs.com of any security breaches or unauthorized access to Your account involving the Platform.
          </p>
          <p>
            Sync EHS reserves the right to audit Your account usage to ensure compliance with this Agreement, with reasonable notice and during business hours.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">7. Data & Privacy</h2>
          <p>
            You own Your data, and Sync EHS will not access, sell, or distribute it without Your consent, except where required by law (e.g., court orders) or to provide the Platform services (e.g., sharing with cloud hosting providers).
          </p>
          <p>
            We may collect anonymized usage metrics (e.g., number of logins, feature usage) to improve the Platform, but this data will not identify You or Your employees.
          </p>
          <p>
            Your data is stored securely using modern encryption and best practices, in compliance with GDPR (for EU users), CCPA (for California residents), and other applicable privacy laws.
          </p>
          <p>
            Sync EHS complies with HIPAA requirements for handling protected health information (PHI). We implement administrative, physical, and technical safeguards to protect PHI, and we will enter into a Business Associate Agreement (BAA) upon request for covered entities.
          </p>
          <p>
            While Sync EHS maintains regular backups, You are responsible for maintaining Your own backups of critical data, as Sync EHS is not liable for data loss due to unforeseen events (e.g., server failures, cyberattacks).
          </p>
          <p>
            You may request export or deletion of Your data at any time by contacting support@syncehs.com, subject to legal retention requirements.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">8. Payment Terms</h2>
          <p>
            Subscriptions are billed monthly or annually in advance, based on Your chosen plan.
          </p>
          <p>
            All payments are final and non-refundable unless required by law or as specified in Your subscription plan.
          </p>
          <p>
            Sync EHS may adjust pricing with 30 days' notice via email or in-app notification; continued use after the change constitutes acceptance of the new pricing.
          </p>
          <p>
            If payment fails after three attempts, Sync EHS may suspend Your account and deny access until payment is resolved.
          </p>
          <p>
            If You dispute a charge, You must notify Sync EHS within 30 days of the charge at support@syncehs.com; we will investigate and respond within 14 business days.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">9. Termination</h2>
          <p>
            You may cancel Your account at any time through the Platform or by contacting support@syncehs.com.
          </p>
          <p>
            Sync EHS may suspend access or terminate Your account if You violate this Agreement, with notice where feasible.
          </p>
          <p>
            After termination, Your data will be retained for 30 days, during which You may request an export in a standard format (e.g., CSV) by contacting support@syncehs.com; after 30 days, data will be permanently deleted unless required by law.
          </p>
          <p>
            If Sync EHS discontinues the Platform entirely, we will provide 60 days' notice and offer a reasonable opportunity to export Your data before deletion.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">10. Indemnity & Liability Waiver</h2>
          <p>
            You agree to indemnify and hold harmless Sync EHS, its affiliates, officers, and employees from:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>
              All claims, damages, or legal actions arising from injuries, deaths, regulatory actions, or compliance failures in Your workplace, whether caused by You, Your employees, or agents.
            </li>
            <li>
              Any misuse, misinterpretation, or unauthorized use of data, graphs, dashboards, or reports generated by the Platform, including by Your employees or agents.
            </li>
          </ul>
          <p>
            Sync EHS's total liability under this Agreement shall not exceed the amount You paid for the Platform in the preceding 12 months. Sync EHS shall not be liable for indirect, incidental, or consequential damages, including lost profits or business interruption.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">11. Governing Law & Dispute Resolution</h2>
          <p>
            This Agreement is governed by the laws of the State of Texas, without regard to conflict of law principles.
          </p>
          <p>
            Disputes arising under this Agreement shall be resolved through binding arbitration in Harris County, TX, under the rules of the American Arbitration Association, unless otherwise agreed by both parties. Alternatively, disputes may be resolved in the state or federal courts of Harris County, TX, and both parties consent to this jurisdiction and venue.
          </p>
          <p>
            You waive any right to participate in a class action lawsuit against Sync EHS.
          </p>
          <p>
            <strong>Equitable Remedies.</strong> You acknowledge that any breach of the intellectual property, restrictions on use, or confidentiality provisions of this Agreement would cause irreparable harm to Sync EHS, LLC for which monetary damages would be an inadequate remedy. In the event of such breach, Sync EHS, LLC shall be entitled to seek injunctive relief, specific performance, or other equitable remedies from any court of competent jurisdiction, without the necessity of posting a bond or proving actual damages, and without regard to any arbitration provision in this Agreement.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">12. Amendments</h2>
          <p>
            Sync EHS may update this Agreement periodically. We will notify You of material changes via email or in-app notification at least 30 days before they take effect.
          </p>
          <p>
            Continued use of the Platform after the effective date of the changes constitutes acceptance of the updated Agreement. If You do not agree to the changes, You may terminate Your account before the effective date.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">13. Service Level Agreement</h2>
          <p>
            Sync EHS strives to maintain 99.9% uptime for the Platform, excluding scheduled maintenance (announced at least 48 hours in advance).
          </p>
          <p>
            Technical support is available via support@syncehs.com during business hours (Monday–Friday, 9 AM–5 PM CST), with a response time of 1–2 business days.
          </p>
          <p>
            Sync EHS is not liable for service interruptions due to factors beyond our control (e.g., internet outages, third-party service failures).
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">14. Force Majeure</h2>
          <p>
            Sync EHS shall not be liable for any failure to perform its obligations under this Agreement due to events beyond its reasonable control, including but not limited to natural disasters, cyberattacks, government regulations, or third-party service failures.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">15. Export Compliance</h2>
          <p>
            You agree to comply with all applicable U.S. export control laws and regulations. You may not use the Platform in any country or by any person embargoed or restricted by the U.S. government.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">Acknowledgment</h2>
          <p>
            By using Sync EHS, You accept this Agreement, acknowledge the disclaimers above, and understand that Sync EHS is a recordkeeping and analytics platform, not a regulatory or legal compliance authority. You also agree to the Privacy Policy and Terms of Service.
          </p>
        </section>
      </div>
    </div>
  );
};

export default LicenseAgreementPage;
